// src/pages/Automations/index.jsx
// Main Automations page - Client outreach automation hub (Light theme)

import React, { useState, useMemo } from 'react';
import { Zap, LayoutList, Activity, Settings2, Bell } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

// Components
import AutomationHeader from './components/AutomationHeader';
import TemplateBasicsModal from './components/TemplateBasicsModal';
import AutomationCategory from './components/AutomationCategory';
import OEPSection from './components/OEPSection';
import TemplateEditor from './components/TemplateEditor';
import ActivityTab from './components/ActivityTab';
import { PendingApprovalsWidget, PendingApprovalsModal } from '../../components/PendingApprovals';

// Hooks
import useAutomations from './hooks/useAutomations';
import useTemplateBasics from './hooks/useTemplateBasics';

// Constants
import { AUTOMATION_CATEGORIES, getAllTriggers } from './constants/triggers';

export default function Automations() {
  // Auth - check if user is founder
  const { user } = useAuth();
  const isFounder = user?.role && ['fmo', 'founder'].includes(user.role);

  // State
  const [activeTab, setActiveTab] = useState('triggers');
  const [showTemplateBasics, setShowTemplateBasics] = useState(false);
  const [showPendingApprovals, setShowPendingApprovals] = useState(false);
  const [editingTrigger, setEditingTrigger] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(['welcome', 'personal']);

  // Hooks
  const {
    userSettings,
    templates,
    activity,
    analytics,
    oepSettings,
    loading,
    saving,
    toggleAutomation,
    updateTemplate,
    createTemplate,
    isEnabled,
    getEnabledCount,
    getTotalEnabled,
    resendAutomation,
    updateOepSendHour,
    updateAutomationSendHour
  } = useAutomations();

  const {
    basics,
    proirpPhone,
    saving: savingBasics,
    saveBasics,
    replaceVariables,
    getAvailableVariables
  } = useTemplateBasics();

  // Computed values
  const totalTriggers = useMemo(() => getAllTriggers().length, []);
  const totalEnabled = useMemo(() => getTotalEnabled(), [getTotalEnabled]);

  // Filter out OEP from regular categories (shown in dedicated section)
  const regularCategories = useMemo(() => {
    return AUTOMATION_CATEGORIES.filter(c => c.key !== 'oep');
  }, []);

  // Handlers
  const handleToggleCategory = (categoryKey) => {
    setExpandedCategories(prev =>
      prev.includes(categoryKey)
        ? prev.filter(k => k !== categoryKey)
        : [...prev, categoryKey]
    );
  };

  const handleEditTemplate = (trigger) => {
    setEditingTrigger(trigger);
  };

  const handleSaveTemplate = async (triggerKey, updates) => {
    console.log('Saving template for', triggerKey, updates);

    try {
      // Get existing template ID if it exists
      const existingTemplate = templates[triggerKey];

      // Save all channels in parallel for speed
      const savePromises = Object.entries(updates).map(async ([channel, data]) => {
        if (existingTemplate && existingTemplate[channel]?.id) {
          // Update existing template
          return updateTemplate(existingTemplate[channel].id, {
            content: data.content,
            subject: data.subject || ''
          });
        } else {
          // Create new template
          const triggerName = editingTrigger?.name || triggerKey;
          return createTemplate({
            name: `${triggerName} - ${channel.toUpperCase()}`,
            type: channel,
            automationKey: triggerKey,
            content: data.content,
            subject: data.subject || ''
          });
        }
      });

      await Promise.all(savePromises);
      // Note: createTemplate/updateTemplate already call fetchTemplates() internally

      // Don't close modal here - let TemplateEditor handle it with confirmation popup
    } catch (error) {
      console.error('Failed to save template:', error);
      alert('Failed to save template: ' + error.message);
      throw error; // Re-throw so TemplateEditor knows it failed
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 py-6 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <AutomationHeader
          totalEnabled={totalEnabled}
          totalTriggers={totalTriggers}
          onOpenTemplateBasics={() => setShowTemplateBasics(true)}
          isFounder={isFounder}
        />

        {/* Tab Navigation */}
        <div className="bg-white rounded-xl shadow-sm mb-6 mt-6">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('triggers')}
              className={`flex items-center gap-2 px-6 py-4 font-semibold border-b-2 transition-colors ${
                activeTab === 'triggers'
                  ? 'text-amber-600 border-amber-500'
                  : 'text-gray-500 border-transparent hover:text-gray-700'
              }`}
            >
              <LayoutList className="w-5 h-5" />
              Triggers
            </button>
            <button
              onClick={() => setActiveTab('activity')}
              className={`flex items-center gap-2 px-6 py-4 font-semibold border-b-2 transition-colors ${
                activeTab === 'activity'
                  ? 'text-amber-600 border-amber-500'
                  : 'text-gray-500 border-transparent hover:text-gray-700'
              }`}
            >
              <Activity className="w-5 h-5" />
              Activity
            </button>
          </div>
        </div>

        {/* Pending Approvals Widget */}
        <PendingApprovalsWidget onViewAll={() => setShowPendingApprovals(true)} />

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-gray-500">Loading automations...</p>
            </div>
          </div>
        ) : activeTab === 'triggers' ? (
          <div className="space-y-6">
            {/* OEP Section (Special) */}
            <OEPSection
              isEnabled={isEnabled}
              onToggle={toggleAutomation}
              onEditTemplate={handleEditTemplate}
              templates={templates}
            />

            {/* Regular Categories */}
            {regularCategories.map((category) => (
              <AutomationCategory
                key={category.key}
                category={category}
                enabledCount={getEnabledCount(category.key)}
                isEnabled={isEnabled}
                onToggle={toggleAutomation}
                onEditTemplate={handleEditTemplate}
                templates={templates}
                defaultExpanded={expandedCategories.includes(category.key)}
                pausedTriggers={[]}  // TODO: Fetch from kill switch API
              />
            ))}
          </div>
        ) : (
          <ActivityTab
            activity={activity}
            analytics={analytics}
            onResend={resendAutomation}
            loading={loading}
          />
        )}
      </div>

      {/* Template Basics Modal */}
      <TemplateBasicsModal
        isOpen={showTemplateBasics}
        onClose={() => setShowTemplateBasics(false)}
        basics={basics}
        onSave={saveBasics}
        saving={savingBasics}
        proirpPhone={proirpPhone}
      />

      {/* Template Editor Modal */}
      <TemplateEditor
        isOpen={!!editingTrigger}
        onClose={() => setEditingTrigger(null)}
        trigger={editingTrigger}
        template={editingTrigger ? templates[editingTrigger.key] : null}
        onSave={handleSaveTemplate}
        saving={saving}
        replaceVariables={replaceVariables}
        availableVariables={getAvailableVariables()}
      />

      {/* Pending Approvals Modal */}
      <PendingApprovalsModal
        isOpen={showPendingApprovals}
        onClose={() => setShowPendingApprovals(false)}
      />
    </div>
  );
}
